<template>
  <div>
    <base-header class="pb-6" type="">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Detalle Pedido Tienda</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-breadcrumb/>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col">
          <div class="card">
            <!-- Card header -->
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">
                    Pedido #{{ order.id }}
                    <badge :type="getStatusColor(order.status_id)" class="ml-2">
                      {{ getStatusDisplayName(order) }}
                    </badge>
                  </h3>
                </div>
                <div class="col text-right">
                  <base-button
                    type="secondary"
                    size="sm"
                    @click="goBack"
                  >
                    <i class="ni ni-bold-left"></i> Volver
                  </base-button>
                </div>
              </div>
            </div>

            <!-- Información general -->
            <div class="card-body">
              <div class="row">
                <div class="col-md-3">
                  <label class="form-control-label">Fecha</label>
                  <p class="form-control-static">{{ formatDate(order.date) }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Catering</label>
                  <p class="form-control-static">{{ order.catering_name }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Creado por</label>
                  <p class="form-control-static">{{ order.creator_name || '-' }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Fecha creación</label>
                  <p class="form-control-static">{{ formatDateTime(order.created_at) }}</p>
                </div>
              </div>

              <!-- Alerta hora límite -->
              <div class="alert alert-warning mt-3" v-if="order.can_edit && order.status_id === 5">
                <i class="ni ni-time-alarm"></i> 
                <strong> Hora límite:</strong> {{ order.cutoff_time }}
                <span v-if="timeRemaining"> - Tiempo restante: <strong>{{ timeRemaining }}</strong></span>
                <div class="mt-2">
                  <strong>Importante:</strong> Si no se aprueba el pedido hasta las <strong>{{ order.cutoff_time }}</strong>, 
                  se aprobará automáticamente con la cantidad solicitada.
                </div>
              </div>

              <div class="alert alert-danger mt-3" v-if="!order.can_edit && order.status_id === 5">
                <i class="ni ni-fat-remove"></i>
                <span v-if="order.cutoff_time">
                  Ya pasó la hora límite ({{ order.cutoff_time }}). Este pedido se aprobará automáticamente.
                </span>
                <span v-else>
                Ya pasó la hora límite. Este pedido se aprobará automáticamente.
                </span>
              </div>
            </div>

            <!-- Tabla de platos -->
            <div class="table-responsive">
              <table class="table align-items-center table-flush">
                <thead class="thead-light">
                  <tr>
                    <th>Plato</th>
                    <th class="text-center">Cantidad Venta Física</th>
                    <th class="text-center">Cantidad Delivery Estimado</th>
                    <th class="text-center">Cantidad Tienda Sugerida</th>
                    <th class="text-center">Total Sugerido</th>
                    <th class="text-center">Total Solicitada</th>
                    <th v-if="order.status_id === 1" class="text-center">Cantidad Total Aprobada</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detail in order.details" :key="detail.id">
                    <td>
                      <strong>{{ detail.meal_name }}</strong>
                      <br>
                      <small class="text-muted">{{ detail.meal_type_name }}</small>
                    </td>
                    <td class="text-center">
                      <badge type="secondary">{{ detail.cantidad_venta_fisica || 0 }}</badge>
                    </td>
                    <td class="text-center">
                      <badge type="info">{{ detail.cantidad_delivery_estimado || 0 }}</badge>
                    </td>
                    <td class="text-center">
                      <base-input
                        v-if="editingSugerencias"
                        type="number"
                        min="0"
                        v-model.number="detail.cantidad_tienda_sugerida"
                        class="mb-0"
                      />
                      <badge v-else type="warning">{{ detail.cantidad_tienda_sugerida || 0 }}</badge>
                    </td>
                    <td class="text-center">
                      <badge type="primary">
                        <strong>{{ getTotalSugerido(detail) }}</strong>
                      </badge>
                      <br>
                      <small class="text-muted">
                        {{ detail.cantidad_delivery_estimado || 0 }} + {{ detail.cantidad_tienda_sugerida || 0 }}
                      </small>
                    </td>
                    <td class="text-center">
                      <badge type="success">{{ getTotalSolicitada(detail) }}</badge>
                      <br>
                      <small class="text-muted">
                        {{ detail.cantidad_delivery_estimado || 0 }} + {{ detail.cantidad_tienda_sugerida || 0 }}
                      </small>
                    </td>
                    <td v-if="order.status_id === 1" class="text-center">
                      <base-input
                        v-if="editing && order.can_edit"
                        type="number"
                        min="0"
                        v-model.number="detail.cantidad_total_aprobada"
                        class="mb-0"
                      />
                      <badge v-else :type="detail.cantidad_total_aprobada > 0 ? 'success' : 'default'">
                        <strong>{{ detail.cantidad_total_aprobada || 0 }}</strong>
                      </badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Acciones -->
            <div class="card-footer" v-if="order.id">
              <!-- Botones para editar sugerencias (solo si puede editar y está en estado Enviado) -->
              <div class="row mb-3" v-if="order.status_id === 5 && order.can_edit">
                <div class="col">
                  <h4>Sugerencias de Catering</h4>
                  <p class="text-muted">Ajusta las cantidades sugeridas para la tienda</p>
                </div>
                <div class="col text-right">
                  <base-button
                    v-if="!editingSugerencias"
                    type="info"
                    @click="startEditingSugerencias"
                  >
                    <i class="ni ni-ruler-pencil"></i> Editar Sugerencias
                  </base-button>

                  <template v-else>
                    <base-button
                      type="success"
                      @click="saveSugerencias"
                      :disabled="saving"
                      class="mr-2"
                    >
                      <i class="ni ni-check-bold"></i> 
                      {{ saving ? 'Guardando...' : 'Guardar Sugerencias' }}
                    </base-button>
                    <base-button
                      type="secondary"
                      @click="cancelEditingSugerencias"
                    >
                      <i class="ni ni-fat-remove"></i> Cancelar
                    </base-button>
                  </template>
                </div>
              </div>
              <hr v-if="order.status_id === 5 && order.can_edit">

              <!-- Botones para aprobar/rechazar (solo si está en estado Enviado y puede editar) -->
              <div class="row" v-if="order.status_id === 5 && order.can_edit">
                <div class="col">
                  <h4>Aprobación de Pedido</h4>
                  <p class="text-muted">Aprobar o rechazar el pedido completo</p>
                </div>
                <div class="col text-right">
                  <base-button
                    type="success"
                    @click="approveAll"
                    :disabled="saving"
                    class="mr-2"
                  >
                    <i class="ni ni-check-bold"></i> 
                    {{ saving ? 'Aprobando...' : 'Aprobar Pedido' }}
                  </base-button>

                  <base-button
                    type="danger"
                    @click="confirmReject"
                    :disabled="saving"
                  >
                    <i class="ni ni-fat-remove"></i> Rechazar Pedido
                  </base-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import backofficeApi from '@/gateways/backoffice-api';
import { toV3 } from '@/utils/apiHelpers';

export default {
  name: 'DetallePedidoTienda',
  data() {
    return {
      loading: false,
      saving: false,
      editing: false,
      editingSugerencias: false,
      order: {
        id: null,
        date: null,
        status_id: null,
        status_name: '',
        catering_name: '',
        creator_name: '',
        created_at: null,
        cutoff_time: null,
        can_edit: false,
        details: []
      },
      originalDetails: [],
      originalSugerencias: [],
      timeRemaining: '',
      intervalId: null
    };
  },
  created() {
    this.loadOrder();
    this.startTimer();
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    async loadOrder() {
      this.loading = true;
      try {
        // Obtener catering_id del usuario autenticado
        const user = this.$store.state.authBackoffice.user;
        const cateringId = user ? user.catering_id : undefined;
        
        const response = await backofficeApi.get(toV3(`physical-orders/${this.$route.params.id}`), {
          params: {
            catering_id: cateringId
          }
        });

        if (response.data.success) {
          this.order = response.data.detail;
          this.originalDetails = JSON.parse(JSON.stringify(this.order.details));
          this.originalSugerencias = JSON.parse(JSON.stringify(this.order.details));
          
          // Actualizar tiempo restante después de cargar el pedido
          if (this.order.cutoff_time && this.order.status_id === 5 && this.order.can_edit) {
            this.updateTimeRemaining();
          }
        }
      } catch (error) {
        console.error('Error loading order:', error);
        this.$notify({
          type: 'danger',
          message: 'Error al cargar el pedido'
        });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    startEditingSugerencias() {
      console.log('Iniciando edición de sugerencias');
      this.editingSugerencias = true;
      this.originalSugerencias = JSON.parse(JSON.stringify(this.order.details));
      console.log('editingSugerencias:', this.editingSugerencias);
    },
    cancelEditingSugerencias() {
      // Restaurar valores originales de sugerencias
      this.order.details.forEach((detail, index) => {
        detail.cantidad_tienda_sugerida = this.originalSugerencias[index].cantidad_tienda_sugerida;
      });
      this.editingSugerencias = false;
    },
    async saveSugerencias() {
      this.saving = true;
      try {
        const user = this.$store.state.authBackoffice.user;
        const cateringId = user ? user.catering_id : undefined;

        const details = this.order.details.map(d => ({
          menu_meal_id: d.menu_meal_id,
          cantidad_tienda_sugerida: d.cantidad_tienda_sugerida || 0
        }));

        const response = await backofficeApi.put(
          toV3(`physical-orders/${this.order.id}/sugerencia`),
          { 
            catering_id: cateringId,
            details 
          }
        );

        if (response.data.success) {
          this.$notify({
            type: 'success',
            message: 'Sugerencias guardadas exitosamente'
          });
          this.editingSugerencias = false;
          this.loadOrder(); // Recargar para ver cantidad_total_sugerida actualizada
        }
      } catch (error) {
        console.error('Error saving sugerencias:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Error al guardar las sugerencias';
        this.$notify({
          type: 'danger',
          message: errorMessage
        });
      } finally {
        this.saving = false;
      }
    },
    startEditing() {
      this.editing = true;
      this.originalDetails = JSON.parse(JSON.stringify(this.order.details));
    },
    cancelEditing() {
      this.editing = false;
      this.order.details = JSON.parse(JSON.stringify(this.originalDetails));
    },
    approveAll() {
      // Aprobar con las cantidades sugeridas (calculadas en tiempo real)
      this.order.details.forEach(detail => {
        detail.cantidad_total_aprobada = this.getTotalSugerido(detail);
      });
      this.saveChanges();
    },
    async saveChanges() {
      if (!confirm('¿Confirmar aprobación del pedido con las cantidades indicadas?')) {
        return;
      }

      this.saving = true;
      try {
        // Obtener catering_id del usuario autenticado
        const user = this.$store.state.authBackoffice.user;
        const cateringId = user ? user.catering_id : undefined;
        
        const details = this.order.details.map(d => ({
          menu_meal_id: d.menu_meal_id,
          cantidad_total_aprobada: d.cantidad_total_aprobada || 0
        }));

        const response = await backofficeApi.post(
          toV3(`physical-orders/${this.order.id}/approve`),
          { 
            catering_id: cateringId,
            details 
          }
        );

        if (response.data.success) {
          this.$notify({
            type: 'success',
            message: 'Pedido aprobado exitosamente'
          });
          this.editing = false;
          this.loadOrder();
        }
      } catch (error) {
        console.error('Error approving order:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Error al aprobar el pedido';
        this.$notify({
          type: 'danger',
          message: errorMessage
        });
      } finally {
        this.saving = false;
      }
    },
    async confirmReject() {
      if (!confirm('¿Estás seguro de rechazar este pedido? Esta acción no se puede deshacer.')) {
        return;
      }

      this.saving = true;
      try {
        // Obtener catering_id del usuario autenticado
        const user = this.$store.state.authBackoffice.user;
        const cateringId = user ? user.catering_id : undefined;
        
        const response = await backofficeApi.post(
          toV3(`physical-orders/${this.order.id}/reject`),
          { catering_id: cateringId }
        );

        if (response.data.success) {
          this.$notify({
            type: 'success',
            message: 'Pedido rechazado'
          });
          this.loadOrder();
        }
      } catch (error) {
        console.error('Error rejecting order:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Error al rechazar el pedido';
        this.$notify({
          type: 'danger',
          message: errorMessage
        });
      } finally {
        this.saving = false;
      }
    },
    startTimer() {
      this.updateTimeRemaining();
      this.intervalId = setInterval(() => {
        this.updateTimeRemaining();
      }, 60000); // Actualizar cada minuto
    },
    updateTimeRemaining() {
      if (!this.order.cutoff_time) return;

      const now = new Date();
      const [hours, minutes] = this.order.cutoff_time.split(':');
      const cutoff = new Date();
      cutoff.setHours(parseInt(hours), parseInt(minutes), 0);

      const diff = cutoff - now;

      if (diff > 0) {
        const hoursLeft = Math.floor(diff / (1000 * 60 * 60));
        const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        this.timeRemaining = `${hoursLeft}h ${minutesLeft}m`;
      } else {
        this.timeRemaining = 'Tiempo agotado';
        this.order.can_edit = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'pedidos-tienda' });
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('es-ES');
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      return new Date(dateTime).toLocaleString('es-ES');
    },
    getStatusColor(statusId) {
      const colors = {
        1: 'success', // Aprobado
        2: 'warning', // Ajustado
        3: 'danger',  // Rechazado
        4: 'default', // Borrador
        5: 'warning'  // Enviado (Por revisar) - amarillo
      };
      return colors[statusId] || 'default';
    },
    getStatusDisplayName(order) {
      // Si está en estado enviado (status_id === 5), mostrar "Por revisar"
      if (order.status_id === 5) {
        return 'Por revisar';
      }
      return order.status_name;
    },
    getTotalSugerido(detail) {
      // Calcula en tiempo real: delivery estimado + tienda sugerida
      const delivery = detail.cantidad_delivery_estimado || 0;
      const tienda = detail.cantidad_tienda_sugerida || 0;
      return delivery + tienda;
    },
    getTotalSolicitada(detail) {
      // Calcula en tiempo real: igual que Total Sugerido (delivery estimado + tienda sugerida)
      const delivery = detail.cantidad_delivery_estimado || 0;
      const tienda = detail.cantidad_tienda_sugerida || 0;
      return delivery + tienda;
    }
  }
};
</script>

